import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, ButtonGroup, CircularProgress, Typography } from '@material-ui/core';
import clsx from 'clsx';

import { ExerciseFileTypes, ExerciseFiles } from 'api/rest/exercises/exercises.types';
import ExercisesHelpers from 'uniqueComponents/Exercises/ExercisesHelpers/ExercisesHelpers';

import { DataFetchingState } from '../../types/DataFetchingState';
import { UrlString } from '../../types/UrlString';
import ExerciseError from '../../uniqueComponents/Exercises/ExerciseError/ExerciseError';
import AudioPlayer from '../AudioPlayer/AudioPlayer';
import ExerciseExternalResources from '../ExerciseExternalResources/ExerciseExternalResources';
import ExerciseNote from '../ExerciseNote/ExerciseNote';
import ImageWithLoaderWrapper from '../ImageWithLoaderWrapper/ImageWithLoaderWrapper';
import useStyles from './ExerciseDisplay.styles';

type Props = {
  notesRef?: any;
  filesToDisplay?: UrlString[];
  /** When set, fullscreen view can switch between question (`t`), answer (`o`), and hint (`p`) images. */
  exerciseFiles?: ExerciseFiles | null;
  selectedFileType?: ExerciseFileTypes;
  fetchingExerciseDataState: DataFetchingState;
  exercisePath?: JSX.Element;
  audioSource?: UrlString;
  manualComponent?: JSX.Element;
  emptyComponent?: JSX.Element;
  currentExerciseId?: string;
};

// TODO simplify on unify
const ExerciseDisplay: React.FC<Props> = ({
  notesRef,
  filesToDisplay,
  exerciseFiles,
  selectedFileType,
  fetchingExerciseDataState,
  exercisePath,
  audioSource,
  manualComponent,
  emptyComponent,
  currentExerciseId,
}) => {
  const [zoomed, setZoomed] = useState(false);
  const [fsContentKind, setFsContentKind] = useState<'t' | 'o' | 'p'>('t');
  const fallbackNotesRef = useRef<{ focus: () => void } | null>(null);
  const resolvedNotesRef = notesRef ?? fallbackNotesRef;

  const focusNote = useCallback(() => {
    resolvedNotesRef.current?.focus?.();
  }, [resolvedNotesRef]);

  useEffect(() => {
    setZoomed(false);
  }, [filesToDisplay]);

  const urlsQuestion = exerciseFiles?.[ExerciseFileTypes.question];
  const urlsAnswer = exerciseFiles?.[ExerciseFileTypes.answer];
  const urlsHint = exerciseFiles?.[ExerciseFileTypes.hint];

  useEffect(() => {
    if (selectedFileType === ExerciseFileTypes.answer && urlsAnswer?.length) setFsContentKind('o');
    else if (selectedFileType === ExerciseFileTypes.hint && urlsHint?.length) setFsContentKind('p');
    else if (urlsQuestion?.length) setFsContentKind('t');
    else if (urlsAnswer?.length) setFsContentKind('o');
    else if (urlsHint?.length) setFsContentKind('p');
  }, [currentExerciseId, selectedFileType, urlsAnswer, urlsHint, urlsQuestion]);

  const fullscreenVariantCount = [urlsQuestion, urlsAnswer, urlsHint].filter(u => u?.length).length;
  const showFullscreenFileTypeToggle = fullscreenVariantCount > 1;

  const fullscreenImageUrls = useMemo((): UrlString[] => {
    if (!exerciseFiles) return filesToDisplay || [];
    const byKind: Record<'t' | 'o' | 'p', UrlString[] | undefined> = {
      t: urlsQuestion,
      o: urlsAnswer,
      p: urlsHint,
    };
    const chosen = byKind[fsContentKind];
    if (chosen?.length) return chosen;
    if (urlsQuestion?.length) return urlsQuestion;
    if (urlsAnswer?.length) return urlsAnswer;
    if (urlsHint?.length) return urlsHint;
    return filesToDisplay || [];
  }, [exerciseFiles, filesToDisplay, fsContentKind, urlsAnswer, urlsHint, urlsQuestion]);

  const onErrorRefresh = async () => {
    // TODO exercises_set refetch
    // const id = exercises[exerciseCursor]?.id;
    // if (reload) reload();
    // const exercise = await queryExercise(id);
    // updateCurrentExercise(exercise);
    // setError(false);
    // @ts-ignore
    window.location.reload(true);
  };

  const isLoading = useMemo(() => fetchingExerciseDataState === 'FETCHING', [fetchingExerciseDataState]);

  const [imageFetching, setImageFetching] = useState(false);

  useEffect(() => {
    if (isLoading) setImageFetching(true);
  }, [isLoading]);

  const onImageFetched = () => {
    setImageFetching(false);
  };

  const classes = useStyles({ isLoading });

  const fullscreenExercisePanel =
    fullscreenImageUrls.length > 0 && currentExerciseId ? (
      <>
        <Box className={classes.fsExerciseToolbar}>
          <ExercisesHelpers dataSource='common_store' focusNote={focusNote} tablesOnly />
          {showFullscreenFileTypeToggle ? (
            <ButtonGroup aria-label='Treść, podpowiedź lub odpowiedź' color='primary' size='small'>
              {urlsQuestion?.length ? (
                <Button onClick={() => setFsContentKind('t')} variant={fsContentKind === 't' ? 'contained' : 'outlined'}>
                  Treść
                </Button>
              ) : null}
              {urlsHint?.length ? (
                <Button onClick={() => setFsContentKind('p')} variant={fsContentKind === 'p' ? 'contained' : 'outlined'}>
                  Podpowiedź
                </Button>
              ) : null}
              {urlsAnswer?.length ? (
                <Button onClick={() => setFsContentKind('o')} variant={fsContentKind === 'o' ? 'contained' : 'outlined'}>
                  Odpowiedź
                </Button>
              ) : null}
            </ButtonGroup>
          ) : null}
        </Box>
        <ImageWithLoaderWrapper loading={false} onLoad={() => undefined} setZoomed={setZoomed} urls={fullscreenImageUrls} zoomed={zoomed} />
      </>
    ) : null;

  /* TODO exercises_set - fallbacks (all completed, no match) */

  if (isLoading) {
    return (
      <div className={clsx(classes.root, classes.centered)}>
        <div className={classes.loader}>
          <CircularProgress size={60} />
        </div>
      </div>
    );
  }

  if (fetchingExerciseDataState === 'ERROR') {
    return (
      <div className={classes.root}>
        <ExerciseError onClick={onErrorRefresh} />
      </div>
    );
  }

  if (fetchingExerciseDataState === 'EMPTY') {
    return (
      <div className={clsx(classes.root, classes.centered)}>
        {emptyComponent || (
          <Typography variant='h2' align='center'>
            Brak zadań do wyświetlenia.
          </Typography>
        )}
      </div>
    );
  }

  if (fetchingExerciseDataState === 'IDLE') {
    return (
      <div className={clsx(classes.root, classes.centered)}>
        {manualComponent || (
          <Typography variant='h2' align='center'>
            Skorzysaj z panelu po lewej aby wyświetlić zadania.
          </Typography>
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={clsx(classes.notVisible, !imageFetching && classes.visible)}>
        {exercisePath || null}
        {audioSource && <AudioPlayer src={audioSource} />}
      </div>
      {/*  @ts-ignore  */}
      <ImageWithLoaderWrapper
        onLoad={onImageFetched}
        loading={imageFetching}
        setZoomed={setZoomed}
        urls={filesToDisplay || []}
        zoomed={zoomed}
      />
      <div className={clsx(classes.notVisible, !imageFetching && classes.visible)}>
        <ExerciseExternalResources dataSource='common_store' />
        <ExerciseNote exerciseId={currentExerciseId} fullscreenExercisePanel={fullscreenExercisePanel} notesRef={resolvedNotesRef} />
      </div>
    </div>
  );
};

export default ExerciseDisplay;
